import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';

const getInfrastructureChip = (infra) => {
  if (infra.status === 'checking') return { label: 'Проверяется', color: 'warning' };
  if (infra.status === 'found') return { label: 'Рядом найден объект', color: 'error' };
  if (infra.status === 'not_found') return { label: 'Объектов рядом нет', color: 'success' };
  if (infra.status === 'error') return { label: 'Ошибка проверки', color: 'default' };
  return { label: 'Ожидание проверки', color: 'default' };
};

const buildInfrastructureErrorSummary = (points) => {
  const unique = new Set();

  points.forEach((point) => {
    if (point.infrastructure.status !== 'error') return;
    const reason = String(point.infrastructure.errorMessage || '').trim();
    if (reason) {
      unique.add(reason);
    }
  });

  return Array.from(unique).slice(0, 2).join(' | ');
};

const PointsTable = ({
  points,
  pointFlags,
  onDeletePoint,
  onDeleteAll,
  onCopyNames,
  deletingAll
}) => {
  const infrastructureErrorSummary = buildInfrastructureErrorSummary(points);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Точки</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onCopyNames}
            disabled={points.length === 0}
          >
            Скопировать названия
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={onDeleteAll}
            disabled={points.length === 0 || deletingAll}
          >
            {deletingAll ? 'Удаление...' : 'Удалить все'}
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>Координаты</TableCell>
              <TableCell>Состояние</TableCell>
              <TableCell align="right">Действие</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {points.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  Список точек пуст
                </TableCell>
              </TableRow>
            ) : (
              points.map((point) => {
                const flags = pointFlags[point.pointKey] || {
                  proximityViolation: false,
                  infrastructureViolation: false
                };

                const infraChip = getInfrastructureChip(point.infrastructure);
                const proximityText = flags.proximityViolation ? 'Нарушение 30м' : '30м: ОК';

                return (
                  <TableRow
                    key={point.pointKey}
                    hover
                    sx={{
                      backgroundColor:
                        flags.proximityViolation || flags.infrastructureViolation
                          ? 'rgba(244,67,54,0.08)'
                          : 'inherit'
                    }}
                  >
                    <TableCell>{point.name}</TableCell>
                    <TableCell>
                      {Number(point.latitude).toFixed(6)}, {Number(point.longitude).toFixed(6)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip size="small" label={proximityText} color={flags.proximityViolation ? 'error' : 'default'} />
                        <Chip size="small" label={infraChip.label} color={infraChip.color} />
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" color="error" onClick={() => onDeletePoint(point.pointKey)}>
                        Удалить
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {points.some((p) => p.infrastructure.status === 'error') && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Для части точек не удалось проверить инфраструктуру. Добавление точек продолжает работать.
          {infrastructureErrorSummary ? ` Причина: ${infrastructureErrorSummary}` : ''}
        </Alert>
      )}
    </Box>
  );
};

export default PointsTable;
